<template>
  <div class="flex flex-col h-full">
    <!-- Header: Title + count + View + Apply + Export -->
    <div class="px-4 py-2.5 bg-white flex items-center gap-3" style="border-bottom: 1px solid #E8E9E9;">
      <h2 class="text-lg font-semibold" style="color: #353537;">Quality Operations</h2>

      <span class="text-xs" style="color: #626264;">
        {{ activeRows.length.toLocaleString() }}
        {{ showGroupTable ? 'groups' : (grain === 'opportunity' ? 'opportunities' : 'actions') }}
      </span>

      <div class="flex-1"></div>

      <!-- View Selector -->
      <div class="relative">
        <div
          v-if="showViewMenu"
          class="fixed inset-0 z-10"
          @click="closeViewMenu"
        ></div>
        <button
          @click="showViewMenu = !showViewMenu"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white hover:bg-gray-50 transition-colors"
          style="border: 1px solid #E8E9E9; color: #353537;"
        >
          <span style="color: #A5A5A6;">View:</span>
          <span>{{ currentViewLabel }}</span>
          <span
            v-if="viewDirty"
            class="text-xs px-1 rounded"
            style="background-color: #FEF3F0; color: #F76734;"
          >modified</span>
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div
          v-if="showViewMenu"
          class="absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg z-20"
          style="border: 1px solid #E8E9E9; min-width: 240px;"
        >
          <div class="px-3 py-1.5 text-xs uppercase tracking-wide" style="color: #A5A5A6;">Presets</div>
          <button
            v-for="v in builtinViews"
            :key="v.key"
            @click="selectView(v.key)"
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between"
            :style="currentViewKey === v.key ? { backgroundColor: '#FEF3F0', color: '#F76734', fontWeight: 600 } : { color: '#353537' }"
          >
            <span>{{ v.name }}</span>
            <span v-if="currentViewKey === v.key" class="text-xs">✓</span>
          </button>

          <div v-if="userViews.length" class="px-3 py-1.5 text-xs uppercase tracking-wide mt-1" style="color: #A5A5A6; border-top: 1px solid #F3F4F6;">My Views</div>
          <div
            v-for="v in userViews"
            :key="v.key"
            class="px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between cursor-pointer"
            :style="currentViewKey === v.key ? { backgroundColor: '#FEF3F0', color: '#F76734', fontWeight: 600 } : { color: '#353537' }"
            @click="selectView(v.key)"
          >
            <span>{{ v.name }}</span>
            <button
              @click.stop="deleteUserView(v.key)"
              class="text-xs px-1.5 hover:bg-gray-200 rounded"
              style="color: #9CA3AF;"
              title="Delete view"
            >×</button>
          </div>

          <div class="px-2 py-2" style="border-top: 1px solid #F3F4F6;">
            <div v-if="!showSaveAsInput" class="flex flex-col gap-1">
              <button
                @click="openSaveAs"
                class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50"
                style="color: #353537;"
              >+ Save current as…</button>
              <button
                v-if="canSaveOverwrite && viewDirty"
                @click="overwriteCurrentView"
                class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50"
                style="color: #353537;"
              >Save changes to "{{ currentView.name }}"</button>
            </div>
            <div v-else class="flex gap-1">
              <input
                v-model="saveAsName"
                @keyup.enter="confirmSaveAs"
                @keyup.escape="cancelSaveAs"
                ref="saveAsInput"
                placeholder="View name"
                class="flex-1 px-2 py-1 text-xs rounded"
                style="border: 1px solid #E8E9E9;"
              />
              <button
                @click="confirmSaveAs"
                :disabled="!saveAsName.trim()"
                class="px-2 py-1 text-xs rounded"
                style="background-color: #F76734; color: white;"
              >Save</button>
              <button
                @click="cancelSaveAs"
                class="px-2 py-1 text-xs rounded"
                style="color: #626264;"
              >×</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Apply Button -->
      <button
        @click="applyPending"
        :disabled="!pendingChanges"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
        :style="pendingChanges
          ? { backgroundColor: '#F76734', color: 'white', border: '1px solid #F76734' }
          : { backgroundColor: '#F9FAFB', color: '#A5A5A6', border: '1px solid #E8E9E9', cursor: 'not-allowed' }"
      >
        Apply{{ pendingChanges ? ' •' : '' }}
      </button>

      <!-- Export -->
      <ExportMenu :disabled="!groupRows.length" :exports="[{ type: 'csv', action: exportCsv, loading: exportLoading }]" />
    </div>

    <!-- Program tabs (primary axis — Quality Gaps vs Medication Adherence
         are operationally distinct, so this is a higher-level cut than the
         other filters). -->
    <div class="px-4 pb-2 border-b border-gray-100">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-0">
          <button
            v-for="tab in programTabs"
            :key="tab.key || 'all'"
            @click="setProgramTab(tab.key)"
            class="text-xs font-medium px-3 py-1.5 border-b-2 transition-colors cursor-pointer"
            :class="activeProgram === tab.key
              ? 'text-orange-600 border-orange-500'
              : 'text-gray-500 border-transparent hover:text-gray-700'"
          >{{ tab.label }}</button>
        </div>
        <!-- Grain toggle: Actions (work grain) ↔ Opportunities (opp grain) -->
        <div class="inline-flex items-center rounded-md bg-gray-100 p-0.5 text-xs">
          <button
            v-for="g in grainOptions"
            :key="g.value"
            @click="setGrain(g.value)"
            class="px-2.5 py-1 rounded cursor-pointer transition-colors"
            :class="grain === g.value
              ? 'bg-white text-gray-800 shadow-sm font-medium'
              : 'text-gray-500 hover:text-gray-700'"
          >{{ g.label }}</button>
        </div>
      </div>
    </div>

    <!-- Filter bar -->
    <Worklist_FilterBar
      storage-key="quality_operations_entity_filter"
      :info-tag-types="infoTagTypes"
      :exclude-types="excludedEntityTypes"
      @filter-changed="onEntityFilterChanged"
    >
      <template #filters>
        <!-- Work-grain filter: which action types were logged -->
        <div v-if="grain === 'work'" class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Action</label>
          <div class="w-48 [&_button]:py-1.5 [&_button]:text-xs">
            <SimpleMultiSelect
              v-model="workTypeFilter"
              :options="workTypeOptions"
              placeholder="All actions"
              :searchable="true"
              @change="markPending"
            />
          </div>
        </div>
        <!-- Opp-grain filters: Status, Routing, Can Call -->
        <div v-if="grain === 'opportunity'" class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Status</label>
          <div class="w-36 [&_button]:py-1.5 [&_button]:text-xs">
            <SimpleMultiSelect
              v-model="statusFilter"
              :options="statusOptions"
              placeholder="All"
              @change="markPending"
            />
          </div>
        </div>
        <div v-if="grain === 'opportunity'" class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Routing</label>
          <div class="w-40 [&_button]:py-1.5 [&_button]:text-xs">
            <SimpleMultiSelect
              v-model="routingTierFilter"
              :options="routingTierOptions"
              placeholder="All"
              @change="markPending"
            />
          </div>
        </div>
        <div v-if="grain === 'opportunity'" class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Engagement</label>
          <div class="w-40 [&_button]:py-1.5 [&_button]:text-xs">
            <SimpleMultiSelect
              v-model="engagementFilter"
              :options="engagementLevelOptions"
              placeholder="All"
              @change="markPending"
            />
          </div>
        </div>
        <label v-if="grain === 'opportunity'"
          class="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap"
          style="color: #A5A5A6;"
          title="Restrict to opportunities on practices with the Quality Patient Call tag (263)"
        >
          <input type="checkbox" v-model="canCallOnly" @change="markPending" class="rounded" />
          Can Call
        </label>
        <div class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Performed by</label>
          <div class="w-48 [&_button]:py-1.5 [&_button]:text-xs">
            <SimpleMultiSelect
              v-model="ownerFilter"
              :options="ownerOptions"
              placeholder="Anyone"
              :searchable="true"
              @change="markPending"
            />
          </div>
        </div>
        <div class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Date</label>
          <input
            type="date"
            v-model="dateFrom"
            @change="onDateInputChange"
            class="text-xs border border-gray-200 rounded px-2 py-1"
          />
          <span class="text-xs whitespace-nowrap" style="color: #A5A5A6;">to</span>
          <input
            type="date"
            v-model="dateTo"
            @change="onDateInputChange"
            class="text-xs border border-gray-200 rounded px-2 py-1"
          />
          <button
            v-for="preset in datePresets"
            :key="preset.key"
            @click="setDatePreset(preset.key)"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :style="datePresetStyle(preset.key)"
          >{{ preset.label }}</button>
          <button
            v-if="dateFrom || dateTo"
            @click="clearDateRange"
            class="text-xs text-gray-400 hover:text-gray-700 cursor-pointer px-1"
            title="Clear date window"
          >✕</button>
        </div>
      </template>
    </Worklist_FilterBar>

    <!-- Opp-grain quick toggles (below main filter row) -->
    <div v-if="grain === 'opportunity'" class="flex items-center gap-4 px-4 pb-2 text-xs text-gray-600">
      <label class="flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" v-model="overdueOnly" @change="markPending" class="rounded" />
        Overdue only
      </label>
      <label class="flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" v-model="unassignedOnly" @change="markPending" class="rounded" />
        Unassigned only
      </label>
      <label class="flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" v-model="staleOnly" @change="markPending" class="rounded" />
        Stale only
      </label>
    </div>

    <!-- Group chips (up to MAX_GROUP_CHIPS) -->
    <div class="flex items-center gap-2 px-4 pb-3 flex-wrap">
      <span class="text-xs text-gray-500 font-medium mr-1">Group by</span>
      <button
        v-for="opt in groupByOptions"
        :key="opt.value"
        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer"
        :class="chipClass(opt.value)"
        @click="toggleChip(opt.value)"
      >
        <span v-if="chipIndex(opt.value) >= 0"
          class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-500 text-white text-[10px] font-bold"
        >{{ chipIndex(opt.value) + 1 }}</span>
        {{ opt.label }}
      </button>
      <span v-if="groupChips.length >= 2" class="text-[10px] text-gray-400 ml-1">
        {{ groupChips.length }} of {{ maxGroupChips }}<template v-if="chartsSuppressed"> · table &amp; CSV only</template>
      </span>
    </div>

    <!-- Methodology banner (per grain) -->
    <div
      class="mx-3 mt-2 px-3 py-1.5 rounded-md text-xs"
      style="background-color: #F8FAFC; border: 1px solid #E2E8F0; color: #475569;"
    >
      <span class="font-semibold" style="color: #353537;">{{ methodologyBanner.title }}.</span>
      {{ methodologyBanner.body }}
    </div>

    <!-- Body: left nav (when drilled) + table + side panel (when detail selected) -->
    <div class="flex flex-1 min-h-0 px-4 pb-4">
      <!-- Left nav (drilled) -->
      <div v-if="selectedGroup" class="w-56 shrink-0 mr-3 flex flex-col">
        <button
          class="flex items-center gap-2 text-xs text-gray-500 hover:text-orange-600 mb-2 cursor-pointer"
          @click="navigateBack"
        >
          <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-[10px]" />
          {{ groupByLabel }}
        </button>
        <div class="rounded-lg border-l-4 border-orange-400 bg-orange-50 px-3 py-2">
          <div class="text-sm font-medium text-gray-800 truncate">
            {{ selectedGroupName }}
          </div>
          <div class="text-xs text-gray-500 mt-0.5">
            {{ selectedGroupSubtitle }}
          </div>
        </div>
      </div>

      <!-- Table column -->
      <div class="flex-1 min-w-0 flex flex-col gap-3">
        <div class="flex-1 min-w-0 overflow-auto border border-gray-200 rounded-lg relative" style="min-height: 200px">
          <div v-if="tableLoading" class="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <div class="flex items-center gap-2">
              <svg class="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <span class="text-sm text-gray-400">Loading...</span>
            </div>
          </div>

          <!-- Stale overlay: filter changes pending — table is showing pre-change data -->
          <div
            v-if="pendingChanges && !tableLoading"
            @click="applyPending"
            class="absolute inset-0 z-10 flex items-center justify-center cursor-pointer"
            style="background-color: rgba(255, 255, 255, 0.65); backdrop-filter: blur(1px);"
          >
            <button
              @click.stop="applyPending"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md shadow-md transition-colors"
              style="background-color: #F76734; color: white;"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
              Apply to refresh
            </button>
          </div>

          <table v-if="!tableLoading && activeRows.length === 0" class="w-full text-xs">
            <thead>
              <tr v-if="columnGroups.length" class="bg-gray-50 border-b border-gray-100">
                <th v-for="(grp, gi) in columnGroups" :key="gi"
                  :colspan="grp.span"
                  class="px-3 py-1 text-[10px] uppercase tracking-wide font-medium text-gray-400 text-center"
                  :class="!grp.filler && gi > 1 ? 'border-l border-gray-200' : ''"
                >{{ grp.label }}</th>
              </tr>
              <tr class="bg-gray-50 border-b border-gray-200">
                <th v-for="col in activeColumns" :key="col.field"
                  class="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap"
                  :class="col.align === 'right' ? 'text-right' : ''"
                >{{ col.header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b border-gray-100">
                <td :colspan="activeColumns.length" class="px-3 py-8 text-center text-gray-400">
                  No {{ showGroupTable ? 'groups' : (isOppGrain ? 'opportunities' : 'actions') }}
                </td>
              </tr>
            </tbody>
          </table>

          <table v-else-if="!tableLoading" class="w-full text-xs">
            <thead>
              <tr v-if="columnGroups.length" class="bg-gray-50 border-b border-gray-100">
                <th v-for="(grp, gi) in columnGroups" :key="gi"
                  :colspan="grp.span"
                  class="px-3 py-1 text-[10px] uppercase tracking-wide font-medium text-gray-400 text-center"
                  :class="!grp.filler && gi > 1 ? 'border-l border-gray-200' : ''"
                >{{ grp.label }}</th>
              </tr>
              <tr class="bg-gray-50 border-b border-gray-200">
                <th v-for="col in activeColumns" :key="col.field"
                  class="px-3 py-2 font-medium text-gray-500 whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                  :class="col.align === 'right' ? 'text-right' : 'text-left'"
                  @click="toggleSort(col.field)"
                >
                  <span class="inline-flex items-center gap-1" :class="col.align === 'right' ? 'justify-end' : ''">
                    {{ col.header }}<span class="text-[9px]">{{ sortIndicator(col.field) }}</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in activeRows"
                :key="showGroupTable ? `g-${idx}` : ((isOppGrain ? row.opportunity_id : row.work_id) || idx)"
                class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                :class="[
                  { 'bg-orange-50': isSelectedRow(row) },
                  (showGroupTable && !drillEnabled) ? 'cursor-default' : 'cursor-pointer',
                ]"
                @click="onRowClick(row)"
              >
                <td v-for="col in activeColumns" :key="col.field"
                  class="px-3 py-2 whitespace-nowrap"
                  :class="[col.align === 'right' ? 'text-right' : '', col.blur || '']"
                >
                  <!-- Group name column with drill arrow.
                       When 2 chips are active and this row shares group_1
                       with the prior row, hide the repeated value to keep
                       the table scannable. -->
                  <template v-if="showGroupTable && col.field === 'group_1' && isGroup1Repeat(idx)">
                    <span class="text-gray-200">·</span>
                  </template>
                  <template v-else-if="showGroupTable && isGroupField(col.field)">
                    <div class="flex items-center gap-1.5">
                      <span class="sensitive font-medium text-gray-800">{{ row[col.field] || 'Unassigned' }}</span>
                      <font-awesome-icon v-if="col.field === 'group_1' && drillEnabled"
                        :icon="['fas', 'caret-right']" class="text-gray-300 text-[10px]"
                      />
                    </div>
                  </template>
                  <!-- Delta: This Week vs Last Week (at same day count) -->
                  <template v-else-if="showGroupTable && col.field === '_delta'">
                    <span v-if="trendDelta(row) === 0" class="text-gray-300">—</span>
                    <span v-else class="inline-flex items-center justify-end gap-0.5" :class="trendClass(row)">
                      <span class="text-[10px] leading-none">{{ trendArrow(row) }}</span>
                      {{ Math.abs(trendDelta(row)).toLocaleString() }}
                    </span>
                  </template>
                  <!-- Dates -->
                  <template v-else-if="col.format === 'datetime'">
                    {{ formatDateTime(row[col.field]) }}
                  </template>
                  <template v-else-if="col.format === 'date'">
                    {{ formatDate(row[col.field]) }}
                  </template>
                  <!-- Default -->
                  <template v-else>
                    {{ formatCell(col, row[col.field]) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Dashboard row (group view only, below the table).
             Main chart is shown on both grains; the Action-Type donut is
             work-grain-only (anchored in work events, meaningless on opps). -->
        <div v-if="showGroupTable" class="flex gap-3 shrink-0">
          <!-- Main chart: grouped (1 chip) or stacked (2 chips) bars.
               Suppressed past 2 chips — the chart has only an x-axis and a
               stack, so it would silently merge rows differing on chips 3-4. -->
          <div class="flex-[2] border border-gray-200 rounded-lg bg-white p-3 min-w-0">
            <template v-if="chartsSuppressed">
              <h3 class="text-xs font-semibold text-gray-700">Chart unavailable</h3>
              <p class="text-[10px] text-gray-400 mb-2">
                Charts show two dimensions. With {{ groupChips.length }} groupings the
                table and CSV carry the full breakdown — remove a grouping to chart it.
              </p>
            </template>
            <template v-else>
              <h3 class="text-xs font-semibold text-gray-700">{{ mainChartTitle }}</h3>
              <p class="text-[10px] text-gray-400 mb-2">{{ mainChartSubtitle }}</p>
            </template>
            <svg v-show="!chartsSuppressed" ref="wowChart" class="w-full block" style="height:180px"></svg>
            <div v-if="isStacked" class="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 mt-1">
              <span v-for="s in stackColors" :key="s.key" class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" :style="{ background: s.color }"></span>
                <span class="truncate max-w-[140px]">{{ s.key }}</span>
              </span>
            </div>
            <div v-else-if="isOppGrain" class="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 mt-1">
              <span class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#10B981"></span>Completed
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#3B82F6"></span>Worked (open)
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#9CA3AF"></span>Untouched
              </span>
            </div>
            <div v-else-if="isDateFiltered" class="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 mt-1">
              <span class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#3B82F6"></span>Actions
              </span>
            </div>
            <div v-else class="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 mt-1">
              <span class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#3B82F6"></span>This Week
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#8B5CF6"></span>Last Week
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#C4B5FD"></span>2 Wks Ago
              </span>
              <span class="inline-flex items-center gap-1">
                <svg width="16" height="4" class="shrink-0">
                  <line x1="0" y1="2" x2="16" y2="2" stroke="#10B981" stroke-width="1.75" stroke-dasharray="3,3" stroke-linecap="round"/>
                </svg>
                Avg/wk (6w)
              </span>
            </div>
          </div>
          <!-- Donut: Volume by Action Type — work grain only -->
          <div v-if="!isOppGrain" class="flex-1 border border-gray-200 rounded-lg bg-white p-3 min-w-0">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-xs font-semibold text-gray-700">Volume by Action Type</h3>
                <p class="text-[10px] text-gray-400 mb-2">Share of all actions</p>
              </div>
              <div class="flex items-center gap-1">
                <button
                  v-for="v in [{k:'chart',i:'chart-pie',t:'Chart'},{k:'table',i:'table-list',t:'Table'}]"
                  :key="v.k"
                  class="px-1.5 py-0.5 rounded text-[10px] border transition-colors cursor-pointer"
                  :class="actionTypeView === v.k
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'"
                  :title="v.t"
                  @click="actionTypeView = v.k"
                >{{ v.t }}</button>
                <button
                  class="ml-1 px-1.5 py-0.5 rounded text-[10px] border border-gray-300
                         bg-white text-gray-500 hover:bg-gray-50 cursor-pointer"
                  title="Copy the table as TSV (paste straight into Excel)"
                  @click="copyActionTypeTable()"
                >{{ actionTypeCopied ? 'Copied' : 'Copy' }}</button>
              </div>
            </div>

            <!-- Chart view -->
            <div v-if="actionTypeView === 'chart'" class="flex items-center gap-3">
              <svg ref="donutChart" width="140" height="140" class="shrink-0"></svg>
              <div class="flex-1 min-w-0 text-[10px] space-y-1 max-h-36 overflow-auto">
                <div v-for="item in actionTypeLegend" :key="item.label"
                     class="flex items-center gap-1.5"
                >
                  <span class="inline-block w-2 h-2 rounded-sm shrink-0" :style="{ background: item.color }"></span>
                  <span class="truncate text-gray-600">{{ item.label }}</span>
                  <span class="ml-auto text-gray-400 tabular-nums">{{ item.value }}</span>
                </div>
                <div v-if="!actionTypeLegend.length" class="text-gray-400">No data</div>
              </div>
            </div>

            <!-- Table view: same numbers, readable/copyable. Shows every action
                 type (no "Other" rollup) so the counts can be recorded directly. -->
            <div v-else class="max-h-44 overflow-auto">
              <table class="w-full text-[10px]">
                <thead class="sticky top-0 bg-gray-50 text-gray-500">
                  <tr class="border-b border-gray-200 text-left">
                    <th class="px-2 py-1 font-medium">Action Type</th>
                    <th class="px-2 py-1 font-medium text-right">Actions</th>
                    <th class="px-2 py-1 font-medium text-right">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in actionTypeTable" :key="r.label"
                      class="border-b border-gray-100 hover:bg-gray-50">
                    <td class="px-2 py-1 text-gray-700">{{ r.label }}</td>
                    <td class="px-2 py-1 text-right tabular-nums text-gray-800">{{ r.count.toLocaleString() }}</td>
                    <td class="px-2 py-1 text-right tabular-nums text-gray-500">{{ r.pct }}%</td>
                  </tr>
                  <tr v-if="!actionTypeTable.length">
                    <td colspan="3" class="px-2 py-4 text-center text-gray-400">No data</td>
                  </tr>
                  <tr v-else class="bg-gray-50 font-semibold text-gray-800">
                    <td class="px-2 py-1">Total</td>
                    <td class="px-2 py-1 text-right tabular-nums">{{ actionTypeTotal.toLocaleString() }}</td>
                    <td class="px-2 py-1 text-right tabular-nums">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Side panel (detail row selected) -->
      <div v-if="selectedDetail && !showGroupTable"
        class="w-80 shrink-0 ml-3 border border-gray-200 rounded-lg bg-white p-4 overflow-auto"
      >
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-gray-800">
            {{ isOppGrain ? 'Opportunity detail' : 'Work detail' }}
          </h3>
          <button @click="selectedDetail = null" class="text-gray-400 hover:text-gray-600 cursor-pointer text-xs">✕</button>
        </div>
        <dl class="text-xs space-y-2">
          <template v-for="(val, label) in detailFields" :key="label">
            <div class="flex justify-between gap-3 border-b border-gray-100 pb-1.5">
              <dt class="text-gray-500">{{ label }}</dt>
              <dd class="text-gray-800 text-right truncate">{{ val }}</dd>
            </div>
          </template>
        </dl>
        <!-- Nested work history for opp-grain drill: which actions were
             logged against this opportunity, newest first. Backend nests
             work_items[] via _fetch_work_batch. -->
        <div v-if="isOppGrain && selectedDetail.work_items?.length" class="mt-4">
          <div class="text-xs font-semibold text-gray-700 mb-2">
            Work history ({{ selectedDetail.work_items.length }})
          </div>
          <ul class="text-[11px] space-y-1">
            <li v-for="w in selectedDetail.work_items" :key="w.work_id"
              class="flex justify-between gap-2 border-b border-gray-100 pb-1">
              <span class="text-gray-600 truncate">
                {{ w.work_type_display_name || w.work_type_name }}
              </span>
              <span class="text-gray-400 whitespace-nowrap">
                {{ formatDateTime(w.work_created_at) }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import {
  BUILTIN_PRESETS,
  resolveGroupChip,
  serializeGroupChip,
  loadUserViews,
  saveUserViews,
  loadLastViewKey,
  saveLastViewKey,
} from './Quality_Operations_Views.js';

// How many group-by chips can be active at once. Raised 2 -> 4 (2026-08-13,
// Tyler Gordon): he needed one row keyed on Practice + Contract + Measure +
// Action to track outcomes by measure, and had tried stitching separate exports
// together because no single export carried all four. The backend never had a
// limit — _get_grouped_rows loops over group_by and emits group_{i+1} for as
// many dimensions as it is given — so this was the only thing in the way.
// Charts still render on the first two chips; the table and CSV carry all of
// them (see chartableChips / isStacked).
const MAX_GROUP_CHIPS = 4;

const DONUT_COLORS = [
  '#3B82F6', '#F97316', '#10B981', '#8B5CF6', '#EF4444',
  '#06B6D4', '#F59E0B', '#EC4899', '#14B8A6', '#64748B',
];

// Engagement-level buckets for the Caseload filter. Composite score (0-5)
// comes from the backend's engagement_scores lookup, which calls
// EngagementScoreService — so the score itself is config-faithful (sub-metric
// weights, max_value, rolling-3 calc). Bucketing is done here.
//
// Order matters: descending by `min`. First bucket whose `min` the score
// reaches wins. `unscored: true` matches practices with no submitted scores.
const ENGAGEMENT_BUCKETS = [
  { value: 'very_engaged', label: 'Very Engaged', min: 4.0 },
  { value: 'semi_engaged', label: 'Semi-Engaged', min: 2.5 },
  { value: 'unengaged',    label: 'Unengaged',    min: 0.0 },
  { value: 'unscored',     label: 'Unscored',     unscored: true },
];

function bucketEngagement(score) {
  if (score == null) return 'unscored';
  for (const b of ENGAGEMENT_BUCKETS) {
    if (b.unscored) continue;
    if (score >= b.min) return b.value;
  }
  return 'unscored';
}

// ============================================================================
// COLUMN DEFINITIONS — work grain only
// ============================================================================

// Work-grain aggregate columns are built in a computed (aggAllColumns) since
// a few headers include the current day-of-week count (e.g. "This Week (3
// days)"). Opportunity-grain aggregates are static — see OPP_AGG_COLUMNS.

const WORK_DETAIL_COLUMNS = [
  { header: 'When', field: 'work_created_at', format: 'datetime' },
  { header: 'Worker', field: 'worker_entity_name', align: 'left', blur: 'sensitive' },
  { header: 'Action', field: 'work_type_display_name', align: 'left' },
  { header: 'Measure', field: 'measure_code' },
  // Line of Business — backed by contract_name (PAYER_NAME is unpopulated;
  // CONTRACT_NAME encodes the LOB, e.g. "Aetna Medicare Advantage New York").
  { header: 'Line of Business', field: 'contract_name', align: 'left' },
  { header: 'Patient', field: 'patient_last_name', align: 'left', blur: 'sensitive' },
  { header: 'Practice', field: 'practice_name', align: 'left', blur: 'sensitive' },
  { header: 'Outcome', field: 'outcome', align: 'left' },
];

// Opportunity-grain (Caseload view) columns. "Aggregate" columns answer
// Danea's funnel: Opps (denominator) → Worked → Closed, plus operational
// flags (overdue / stale).
const OPP_AGG_COLUMNS = [
  { header: 'Opps', field: 'opportunity_count', align: 'right' },
  { header: 'Patients', field: 'patient_count', align: 'right' },
  { header: 'Active', field: 'active_count', align: 'right' },
  { header: 'Completed', field: 'completed_count', align: 'right' },
  { header: 'Overdue', field: 'overdue_count', align: 'right' },
  { header: 'Stale', field: 'stale_count', align: 'right' },
  // Count of opportunities that have any work logged AND aren't completed.
  // Pairs with the Caseload chart's "Worked" segment (same meaning).
  { header: 'Worked', field: 'worked_open_count', align: 'right' },
  // Total count of work events — sum of per-opp work counts.
  { header: 'Events', field: 'total_work', align: 'right' },
];

const OPP_DETAIL_COLUMNS = [
  { header: 'Patient', field: 'patient_last_name', align: 'left', blur: 'sensitive' },
  { header: 'Measure', field: 'measure_code' },
  // Line of Business — backed by contract_name (see WORK_DETAIL_COLUMNS note).
  { header: 'Line of Business', field: 'contract_name', align: 'left' },
  { header: 'Status', field: 'opp_status' },
  { header: 'Routing', field: 'routing_tier' },
  { header: 'Practice', field: 'practice_name', align: 'left', blur: 'sensitive' },
  { header: 'Owner', field: 'owner_entity_name', align: 'left', blur: 'sensitive' },
  { header: 'Days', field: 'days_in_pipeline', align: 'right' },
  { header: 'Last Work', field: 'last_work_at', align: 'right', format: 'datetime' },
  { header: 'Work', field: 'total_work', align: 'right' },
];

export default {
  name: 'Quality_Operations',
  components: { FontAwesomeIcon, Worklist_FilterBar, SimpleMultiSelect, ExportMenu },

  data() {
    return {
      // Grain: 'work' (velocity / action log) | 'opportunity' (caseload funnel)
      grain: 'work',

      // Up to MAX_GROUP_CHIPS; charts use the first two.
      groupChips: ['owner'],
      selectedGroup: null,
      selectedDetail: null,

      groupRows: [],
      detailRows: [],
      actionTypeRows: [],
      // 'chart' = donut + legend, 'table' = readable/copyable counts. Selina
      // (Vytalize) was transcribing the legend numbers by hand for reporting.
      actionTypeView: 'chart',
      actionTypeCopied: false,
      groupLoading: false,
      detailLoading: false,

      sortCol: 'this_week_count',
      sortDir: 'desc',

      // Shared filters
      activeProgram: null, // null = All; else 'quality_gaps' | 'medication_adherence'
      workTypeFilter: [],
      ownerFilter: [],
      dateFrom: '',
      dateTo: '',

      // Opp-grain filters (ignored on work grain)
      statusFilter: [],
      routingTierFilter: [],
      engagementFilter: [],
      canCallOnly: true,       // default ON — per Danea's requirement
      overdueOnly: false,
      unassignedOnly: false,
      staleOnly: false,

      // Lookups
      entityTypes: [],
      workTypes: [],
      owners: [],
      // Map of practice entity_id → composite engagement score (0-5, or null
      // for unscored practices). Loaded once on mount; the Engagement filter
      // buckets these client-side via ENGAGEMENT_BUCKETS and sends matching
      // practice_ids to the backend as practiceIdFilter on each refetch.
      engagementScores: [],

      // Entity filter state (from Worklist_FilterBar)
      entityFilters: null,
      infoTagFilters: null,
      myPractices: false,

      // Config (unchanging)
      infoTagTypes: ['Implementation Status'],
      excludedEntityTypes: ['Patient', 'Staff', 'TIN', 'Location', 'Contract', 'CPY', 'Start Year', 'Vendor'],

      exportLoading: false,

      // Date presets (parallel to Coding)
      activeDatePreset: null,
      datePresets: [
        { key: 'mtd', label: 'MTD' },
        { key: 'last_month', label: 'Last Month' },
        { key: 'ytd', label: 'YTD' },
      ],

      // Manual Apply + saved views
      pendingChanges: false,
      userViews: loadUserViews(),
      currentViewKey: null,
      showViewMenu: false,
      showSaveAsInput: false,
      saveAsName: '',
    };
  },

  computed: {
    programTabs() {
      return [
        { key: null, label: 'All' },
        { key: 'quality_gaps', label: 'Quality Gaps' },
        { key: 'medication_adherence', label: 'Medication Adherence' },
      ];
    },
    grainOptions() {
      return [
        { value: 'work', label: 'Actions' },
        { value: 'opportunity', label: 'Opportunities' },
      ];
    },
    isOppGrain() {
      return this.grain === 'opportunity';
    },
    statusOptions() {
      return [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'expired', label: 'Expired' },
      ];
    },
    routingTierOptions() {
      return [
        { value: 'chart_review', label: 'Chart Review' },
        { value: 'outreach', label: 'Patient Outreach' },
        { value: 'practice_list', label: 'Send List to Practice' },
      ];
    },
    engagementLevelOptions() {
      // Static — defined alongside the bucketing function at the top of the
      // file. Tuning the cutoffs is a one-line edit to ENGAGEMENT_BUCKETS.
      return ENGAGEMENT_BUCKETS.map(b => ({ value: b.value, label: b.label }));
    },
    workTypeOptions() {
      return this.workTypes.map(wt => ({
        value: wt.work_type_id,
        label: wt.work_type_display_name,
      }));
    },
    ownerOptions() {
      return this.owners.map(o => ({ value: o.id, label: o.name }));
    },
    groupByOptions() {
      const opts = [];
      // Program breakdown only makes sense on the All tab — a specific
      // program tab would show a single-row group.
      if (this.activeProgram == null) {
        opts.push({ value: 'program', label: 'Program' });
      }
      opts.push(
        { value: 'contract', label: 'Contract' },
        { value: 'payer', label: 'Payer' },
      );
      const practice = this.entityTypes.find(et => et.type_name === 'Practice');
      if (practice && practice.entity_count > 0) {
        opts.push({ value: practice.id, label: 'Practice' });
      }
      opts.push({ value: 'measure', label: 'Measure' });
      if (this.isOppGrain) {
        // Caseload: no Action Type (no work events at this grain).
        opts.push(
          { value: 'owner', label: 'Owner' },
          { value: 'status', label: 'Status' },
          { value: 'routing_tier', label: 'Routing' },
        );
      } else {
        opts.push(
          { value: 'owner', label: 'Worker' },
          { value: 'work_type', label: 'Action' },
        );
      }
      return opts;
    },
    showGroupTable() {
      return !this.selectedGroup;
    },
    tableLoading() {
      return this.showGroupTable ? this.groupLoading : this.detailLoading;
    },
    groupByLabel() {
      return this.groupChips.map(v => this.chipLabel(v)).join(' × ') || 'Groups';
    },
    selectedGroupName() {
      if (!this.selectedGroup) return '';
      return this.groupChips
        .map((_, i) => this.selectedGroup[`group_${i + 1}`] || 'Unassigned')
        .join(' / ');
    },
    selectedGroupSubtitle() {
      if (!this.selectedGroup) return '';
      const label = this.isOppGrain ? 'opportunities' : 'actions';
      const field = this.isOppGrain ? 'opportunity_count' : 'work_count';
      return `${(this.selectedGroup[field] || 0).toLocaleString()} ${label}`;
    },
    activeColumns() {
      if (this.showGroupTable) {
        const groupCols = this.groupChips.map((chip, i) => ({
          header: this.chipLabel(chip),
          field: `group_${i + 1}`,
          align: 'left',
        }));
        return [...groupCols, ...this.aggAllColumns];
      }
      return this.isOppGrain ? OPP_DETAIL_COLUMNS : WORK_DETAIL_COLUMNS;
    },
    activeRows() {
      const data = this.showGroupTable ? this.groupRows : this.detailRows;
      if (!this.sortCol) return data;
      return this.sortData(data, this.sortCol, this.sortDir);
    },
    maxGroupChips() {
      return MAX_GROUP_CHIPS;
    },
    primaryGroupLabel() {
      return this.groupChips.length ? this.chipLabel(this.groupChips[0]).toLowerCase() : 'group';
    },
    // Drill-in is a two-level state machine (selectedGroup -> selectedDetail).
    // Past two chips the user is grouping, not drilling, so row clicks are inert
    // rather than dropping them into a view that can't represent the extra keys.
    drillEnabled() {
      return this.groupChips.length <= 2;
    },
    daysIntoWeek() {
      // 1 = Monday through 7 = Sunday (matches Snowflake ISO week)
      const d = new Date().getDay();
      return d === 0 ? 7 : d;
    },
    dayLabel() {
      const d = this.daysIntoWeek;
      return `${d} day${d === 1 ? '' : 's'}`;
    },
    isDateFiltered() {
      return !!(this.dateFrom || this.dateTo);
    },
    windowDays() {
      // Inclusive day count of the filter window. null when no lower bound
      // is set — velocity avg can't be computed without a start.
      if (!this.dateFrom) return null;
      const start = new Date(this.dateFrom);
      const end = this.dateTo ? new Date(this.dateTo) : new Date();
      if (isNaN(start) || isNaN(end)) return null;
      const ms = end.setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0);
      return Math.max(Math.floor(ms / 86400000) + 1, 0);
    },
    aggregateColumns() {
      if (this.isOppGrain) return OPP_AGG_COLUMNS;
      return [
        { header: 'Actions', field: 'work_count', align: 'right' },
        { header: 'Patients', field: 'patient_count', align: 'right' },
        { header: 'Action Types', field: 'work_type_count', align: 'right' },
      ];
    },
    infoColumns() {
      return [
        {
          header: this.isOppGrain ? 'Last Worked' : 'Last',
          field: 'last_work_at', align: 'right', format: 'datetime',
        },
      ];
    },
    velocityColumns() {
      // Caseload has no velocity block — the mental mode is "where's the
      // funnel right now?", not "what's the pace this week?".
      if (this.isOppGrain) return [];
      if (!this.isDateFiltered) {
        const dl = this.dayLabel;
        return [
          { header: `This Week (${dl})`, field: 'this_week_count', align: 'right' },
          { header: `Last Week (at ${dl})`, field: 'last_week_at_this_time_count', align: 'right' },
          { header: 'Δ', field: '_delta', align: 'right' },
          { header: 'Last Week', field: 'last_week_count', align: 'right' },
          { header: '2 Wks Ago', field: 'two_weeks_ago_count', align: 'right' },
          { header: 'Avg/wk (6w)', field: 'avg_per_week', align: 'right' },
        ];
      }
      // Filtered window — a single avg/wk only if window ≥ 7 days.
      if (this.windowDays != null && this.windowDays >= 7) {
        return [
          { header: 'Avg/wk', field: 'avg_per_week', align: 'right' },
        ];
      }
      return [];
    },
    aggAllColumns() {
      return [...this.aggregateColumns, ...this.infoColumns, ...this.velocityColumns];
    },
    columnGroups() {
      // Header-group metadata rendered above the regular header row in the
      // grouped table. Detail view returns an empty array → single-row header.
      if (!this.showGroupTable) return [];
      // Default window is 90 days when user hasn't set From/To — call that
      // out in the group header so the numbers aren't read as all-time.
      const aggregateLabel = this.isDateFiltered
        ? 'Aggregate'
        : 'Aggregate (last 90 days)';
      const groups = [
        { label: '', span: this.groupChips.length, filler: true },
        { label: aggregateLabel, span: this.aggregateColumns.length },
        { label: 'Info', span: this.infoColumns.length },
      ];
      const velSpan = this.velocityColumns.length;
      if (velSpan > 0) {
        groups.push({ label: 'Velocity', span: velSpan });
      }
      return groups;
    },
    secondaryGroupLabel() {
      return this.groupChips.length > 1 ? this.chipLabel(this.groupChips[1]).toLowerCase() : '';
    },
    // Charts are two-dimensional by construction (x = chip 1, stack = chip 2).
    // With 3-4 chips the stacked chart would silently collapse rows that differ
    // only on chips 3-4, so it is suppressed and the table carries the detail.
    isStacked() {
      return this.groupChips.length === 2;
    },
    // True when more chips are active than the charts can represent.
    chartsSuppressed() {
      return this.groupChips.length > 2;
    },
    mainChartTitle() {
      if (this.isStacked) {
        return this.isOppGrain ? 'Caseload Breakdown' : 'Activity Breakdown';
      }
      if (this.isOppGrain) return 'Caseload Funnel';
      if (this.isDateFiltered) return 'Activity in Window';
      return 'Week-over-Week Activity';
    },
    mainChartSubtitle() {
      const primary = this.primaryGroupLabel;
      const plural = (count) => (count === 1 ? '' : 's');
      if (this.isStacked) {
        const n = this.stackedChartData.rows.length;
        const metric = this.isOppGrain ? 'opportunities' : 'actions';
        return `Top ${n} ${primary}${plural(n)} by total ${metric}, stacked by ${this.secondaryGroupLabel}`;
      }
      if (this.isOppGrain) {
        const n = this.caseloadChartData.length;
        return `Top ${n} ${primary}${plural(n)} by opportunities — Opps → Worked → Completed`;
      }
      const n = this.wowChartData.length;
      if (this.isDateFiltered) {
        return `Top ${n} ${primary}${plural(n)} by total actions in window`;
      }
      return `Top ${n} ${primary}${plural(n)} by this week`;
    },
    stackedChartData() {
      // Only meaningful when 2 chips. Produces rows shaped for d3.stack():
      //   [{ group_1, <g2-key-1>: n, <g2-key-2>: n, ... }, ...]
      // Sorted by total desc, top 10. Metric = opportunity_count on opp
      // grain, work_count on work grain.
      if (!this.isStacked) return { rows: [], keys: [] };
      const metricField = this.isOppGrain ? 'opportunity_count' : 'work_count';
      const byG1 = {};
      for (const row of this.groupRows) {
        const g1 = row.group_1 || 'Unassigned';
        const g2 = row.group_2 || 'Unassigned';
        if (!byG1[g1]) byG1[g1] = { group_1: g1, _total: 0 };
        const val = row[metricField] || 0;
        byG1[g1][g2] = (byG1[g1][g2] || 0) + val;
        byG1[g1]._total += val;
      }
      const top = Object.values(byG1)
        .sort((a, b) => b._total - a._total)
        .slice(0, 10);
      const keySet = new Set();
      for (const entry of top) {
        for (const k of Object.keys(entry)) {
          if (k !== 'group_1' && k !== '_total') keySet.add(k);
        }
      }
      // Order keys by total volume across all top rows so colors are stable-ish
      const keys = Array.from(keySet).sort((a, b) => {
        const ta = top.reduce((s, r) => s + (r[a] || 0), 0);
        const tb = top.reduce((s, r) => s + (r[b] || 0), 0);
        return tb - ta;
      });
      // Fill missing keys with 0 so d3.stack has uniform shape
      for (const entry of top) {
        for (const k of keys) if (entry[k] == null) entry[k] = 0;
      }
      return { rows: top, keys };
    },
    stackColors() {
      return this.stackedChartData.keys.map((k, i) => ({
        key: k,
        color: DONUT_COLORS[i % DONUT_COLORS.length],
      }));
    },
    caseloadChartData() {
      // Per group_1: funnel slices (Completed / Worked-open / Untouched)
      // summing to opportunity_count. Top 10 by opportunity_count desc.
      // Driven by already-loaded group rows — no separate fetch.
      if (!this.isOppGrain) return [];
      const byG1 = {};
      for (const row of this.groupRows) {
        const key = row.group_1 || 'Unassigned';
        if (!byG1[key]) {
          byG1[key] = {
            group_1: key,
            opportunity_count: 0,
            completed_count: 0,
            worked_open_count: 0,
            untouched_count: 0,
          };
        }
        byG1[key].opportunity_count += row.opportunity_count || 0;
        byG1[key].completed_count += row.completed_count || 0;
        byG1[key].worked_open_count += row.worked_open_count || 0;
        byG1[key].untouched_count += row.untouched_count || 0;
      }
      return Object.values(byG1)
        .sort((a, b) => b.opportunity_count - a.opportunity_count)
        .slice(0, 10);
    },
    wowChartData() {
      // Aggregate by group_1 so the chart works whether 1 or 2 chips are set.
      // When a date window is applied, we show a single-series Actions bar
      // instead of a week-over-week comparison — only work_count is needed.
      const byG1 = {};
      for (const row of this.groupRows) {
        const key = row.group_1 || 'Unassigned';
        if (!byG1[key]) {
          byG1[key] = {
            group_1: key,
            work_count: 0,
            this_week_count: 0,
            last_week_count: 0,
            two_weeks_ago_count: 0,
            avg_per_week: 0,
          };
        }
        byG1[key].work_count += row.work_count || 0;
        byG1[key].this_week_count += row.this_week_count || 0;
        byG1[key].last_week_count += row.last_week_count || 0;
        byG1[key].two_weeks_ago_count += row.two_weeks_ago_count || 0;
        byG1[key].avg_per_week += Number(row.avg_per_week) || 0;
      }
      const sortField = this.isDateFiltered ? 'work_count' : 'this_week_count';
      return Object.values(byG1)
        .sort((a, b) => b[sortField] - a[sortField])
        .slice(0, 10);
    },
    donutData() {
      const sorted = this.actionTypeRows
        .slice()
        .sort((a, b) => (b.work_count || 0) - (a.work_count || 0));
      if (sorted.length <= 10) return sorted;
      // Preserve the long tail as an explicit "Other" slice so the donut
      // reflects 100% of actions rather than silently dropping ranks 10+.
      const top = sorted.slice(0, 9);
      const otherCount = sorted.slice(9).reduce((s, r) => s + (r.work_count || 0), 0);
      return [
        ...top,
        { group_1: 'Other', work_count: otherCount, _isOther: true },
      ];
    },
    /**
     * Every action type with its count and share — no top-10 "Other" rollup,
     * because this view exists to be read off / copied for reporting.
     */
    actionTypeTable() {
      const rows = this.actionTypeRows
        .slice()
        .sort((a, b) => (b.work_count || 0) - (a.work_count || 0));
      const total = rows.reduce((sum, r) => sum + (r.work_count || 0), 0);
      return rows.map(r => {
        const count = r.work_count || 0;
        return {
          label: r.group_1 || 'Unassigned',
          count,
          pct: total ? ((count / total) * 100).toFixed(1) : '0.0',
        };
      });
    },
    actionTypeTotal() {
      return this.actionTypeRows.reduce((sum, r) => sum + (r.work_count || 0), 0);
    },
    actionTypeLegend() {
      return this.donutData.map((r, i) => ({
        label: r.group_1 || 'Unassigned',
        value: (r.work_count || 0).toLocaleString(),
        color: DONUT_COLORS[i % DONUT_COLORS.length],
      }));
    },
    methodologyBanner() {
      const banners = {
        work: {
          title: 'Methodology: action-level activity log',
          body: 'Each row represents an action logged against a quality measure (gap closure, outreach attempt, list send, etc.). Worker reflects the person who performed the action. Use this view to measure throughput, action mix, and per-worker productivity.',
        },
        opportunity: {
          title: 'Methodology: opportunity-level pipeline ownership',
          body: 'Each row represents a quality opportunity (patient × measure). Owner reflects the assigned routing tier (chart review, patient outreach, or send-list-to-practice). Use this view to understand the funnel — Opps → Worked → Completed — plus overdue and stale backlog.',
        },
      };
      return banners[this.grain];
    },

    // -- Saved views --
    builtinViews() {
      return BUILTIN_PRESETS.map(p => ({ ...p, builtin: true }));
    },
    allViews() {
      return [
        ...this.builtinViews,
        ...this.userViews.map(v => ({ ...v, builtin: false })),
      ];
    },
    currentView() {
      return this.allViews.find(v => v.key === this.currentViewKey) || null;
    },
    currentViewLabel() {
      return this.currentView ? this.currentView.name : 'Custom';
    },
    canSaveOverwrite() {
      return !!this.currentView && !this.currentView.builtin;
    },
    viewDirty() {
      if (!this.currentView) return false;
      return JSON.stringify(this._captureCurrentState()) !== JSON.stringify(this.currentView.state);
    },

    detailFields() {
      if (!this.selectedDetail) return {};
      const r = this.selectedDetail;
      if (this.isOppGrain) {
        return {
          Patient: [r.patient_last_name, r.patient_first_name].filter(Boolean).join(', ') || r.patient_identifier || '—',
          Program: this.programLabel(r.program),
          Measure: [r.measure_code, r.measure_name].filter(Boolean).join(' — '),
          'Line of Business': r.contract_name || '—',
          Status: r.opp_status || '—',
          Routing: this.routingTierLabel(r.routing_tier),
          Practice: r.practice_name || '—',
          Owner: r.owner_entity_name || '—',
          'Current Task': [r.current_task_type, r.current_task_status].filter(Boolean).join(' / ') || '—',
          'Days in pipeline': r.days_in_pipeline != null ? r.days_in_pipeline : '—',
          'Last worked': this.formatDateTime(r.last_work_at),
          'Total work': r.total_work != null ? r.total_work : '—',
          Overdue: r.is_overdue ? 'Yes' : '',
          Stale: r.is_stale ? 'Yes' : '',
        };
      }
      return {
        When: this.formatDateTime(r.work_created_at),
        Worker: r.worker_entity_name || '—',
        Action: r.work_type_display_name || r.work_type_name,
        Program: this.programLabel(r.program),
        Measure: [r.measure_code, r.measure_name].filter(Boolean).join(' — '),
        'Line of Business': r.contract_name || '—',
        Patient: [r.patient_last_name, r.patient_first_name].filter(Boolean).join(', ') || r.patient_identifier || '—',
        Practice: r.practice_name || '—',
        Outcome: r.outcome || '—',
        Duration: this.formatDuration(r.duration_seconds),
        Notes: r.notes || '—',
      };
    },
  },

  watch: {
    wowChartData() {
      this.$nextTick(() => this.renderWowChart());
    },
    caseloadChartData() {
      this.$nextTick(() => this.renderWowChart());
    },
    stackedChartData() {
      this.$nextTick(() => this.renderWowChart());
    },
    donutData() {
      this.$nextTick(() => this.renderDonut());
    },
    // v-if destroys the <svg> ref when the table view is shown, so the donut
    // must be redrawn on the way back.
    actionTypeView(view) {
      if (view === 'chart') this.$nextTick(() => this.renderDonut());
    },
    showGroupTable(isGroup) {
      if (isGroup) {
        this.$nextTick(() => {
          this.renderWowChart();
          this.renderDonut();
        });
      }
    },
    isDateFiltered() {
      // Sort column may have pointed at a velocity field that's no longer
      // visible — drop back to the aggregate default.
      const hiddenWhenFiltered = [
        'this_week_count', 'last_week_count', 'two_weeks_ago_count',
        'last_week_at_this_time_count', '_delta',
      ];
      if (this.isDateFiltered && hiddenWhenFiltered.includes(this.sortCol)) {
        this.sortCol = 'work_count';
        this.sortDir = 'desc';
      }
      this.$nextTick(() => this.renderWowChart());
    },
  },

  methods: {
    // -- Chip management --
    chipClass(value) {
      const active = this.groupChips.includes(value);
      return active
        ? 'bg-orange-50 text-orange-700 border-orange-300'
        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300';
    },
    chipIndex(value) {
      return this.groupChips.indexOf(value);
    },
    chipLabel(value) {
      const opt = this.groupByOptions.find(o => o.value === value);
      return opt ? opt.label : String(value);
    },
    toggleChip(value) {
      const idx = this.groupChips.indexOf(value);
      if (idx >= 0) {
        this.groupChips.splice(idx, 1);
      } else if (this.groupChips.length < MAX_GROUP_CHIPS) {
        this.groupChips.push(value);
      } else {
        this.groupChips.shift();
        this.groupChips.push(value);
      }
      this.selectedGroup = null;
      this.selectedDetail = null;
      this.markPending();
    },

    // -- Navigation --
    async drillInto(group) {
      this.selectedGroup = group;
      this.selectedDetail = null;
      await this.fetchDetails();
    },
    navigateBack() {
      this.selectedGroup = null;
      this.selectedDetail = null;
      this.detailRows = [];
    },
    onRowClick(row) {
      if (this.showGroupTable) {
        if (!this.drillEnabled) return;
        this.drillInto(row);
      } else {
        this.selectedDetail = row;
      }
    },
    isSelectedRow(row) {
      if (!this.selectedDetail) return false;
      if (this.isOppGrain) {
        return this.selectedDetail.opportunity_id === row.opportunity_id;
      }
      return this.selectedDetail.work_id === row.work_id;
    },

    routingTierLabel(tier) {
      if (tier === 'chart_review') return 'Chart Review';
      if (tier === 'outreach') return 'Patient Outreach';
      if (tier === 'practice_list') return 'Send List to Practice';
      return tier || '—';
    },

    isGroupField(field) {
      return /^group_\d+$/.test(field);
    },
    isGroup1Repeat(idx) {
      // Only relevant when two chips are active — with one chip every row's
      // group_1 is unique. If two chips and the prior row carries the same
      // group_1, we've got a consecutive repeat worth hiding.
      if (this.groupChips.length < 2 || idx === 0) return false;
      const rows = this.activeRows;
      return rows[idx - 1] && rows[idx - 1].group_1 === rows[idx].group_1;
    },

    // -- Data fetching --
    _buildPayload(extra = {}) {
      const payload = {
        grain: this.grain,
        groupBy: this.groupChips,
        startRow: 0,
        endRow: 500,
        ...extra,
      };
      if (this.entityFilters) payload.entity_filters = this.entityFilters;
      if (this.infoTagFilters?.length) payload.infoTagFilters = this.infoTagFilters;
      if (this.myPractices) payload.my_practices = true;
      if (this.activeProgram) payload.programFilter = [this.activeProgram];
      if (this.ownerFilter.length) payload.ownerFilter = this.ownerFilter;
      if (this.dateFrom || this.dateTo) {
        payload.dateRange = {};
        if (this.dateFrom) payload.dateRange.start = this.dateFrom;
        if (this.dateTo) payload.dateRange.end = this.dateTo;
      }
      if (this.isOppGrain) {
        if (this.statusFilter.length) payload.statusFilter = this.statusFilter;
        if (this.routingTierFilter.length) payload.routingTierFilter = this.routingTierFilter;
        if (this.engagementFilter.length) {
          // Bucket each practice's score client-side and send the matching
          // practice IDs to the backend as a generic practice-id filter.
          // Sentinel `[0]` when no practice matches: backend treats an
          // empty list as "filter not applied" (matches every row), which
          // is the wrong semantic here — `[0]` is an unreachable ID so
          // PRACTICE_ENTITY_ID IN (0) correctly returns zero rows.
          const picked = new Set(this.engagementFilter);
          const ids = this.engagementScores
            .filter(s => picked.has(bucketEngagement(s.score)))
            .map(s => s.entity_id);
          payload.practiceIdFilter = ids.length ? ids : [0];
        }
        payload.canCallOnly = this.canCallOnly;
        if (this.overdueOnly) payload.overdueOnly = true;
        if (this.unassignedOnly) payload.unassignedOnly = true;
        if (this.staleOnly) payload.staleOnly = true;
      } else {
        if (this.workTypeFilter.length) payload.workTypeFilter = this.workTypeFilter;
      }
      return payload;
    },

    async fetchGroups() {
      this.groupLoading = true;
      try {
        const res = await this.$axios.post('/api/quality/operations/', this._buildPayload({
          groupKeys: null,
        }));
        this.groupRows = res.data.rows || [];
      } catch (error) {
        console.error('Failed to fetch groups:', error);
        this.groupRows = [];
      } finally {
        this.groupLoading = false;
      }
    },

    async fetchActionTypeDistribution() {
      // Independent of current grouping — always by work_type — feeds the donut.
      try {
        const payload = this._buildPayload({ groupBy: ['work_type'], groupKeys: null });
        const res = await this.$axios.post('/api/quality/operations/', payload);
        this.actionTypeRows = res.data.rows || [];
      } catch (error) {
        console.error('Failed to fetch action type distribution:', error);
        this.actionTypeRows = [];
      }
    },

    async fetchDetails() {
      this.detailLoading = true;
      try {
        const groupKeys = this.groupChips.map((_, i) => {
          const idCol = `group_${i + 1}_id`;
          return this.selectedGroup[idCol] != null
            ? this.selectedGroup[idCol]
            : this.selectedGroup[`group_${i + 1}`];
        });
        const res = await this.$axios.post('/api/quality/operations/', this._buildPayload({
          groupKeys,
        }));
        this.detailRows = res.data.rows || [];
      } catch (error) {
        console.error('Failed to fetch details:', error);
        this.detailRows = [];
      } finally {
        this.detailLoading = false;
      }
    },

    refetchCurrentLevel() {
      if (this.selectedGroup) {
        this.fetchDetails();
      } else {
        this.fetchGroups();
        // Action-type donut is work-grain only — opp grain has no work_type
        // dimension, so skip that fetch when on Caseload.
        if (!this.isOppGrain) {
          this.fetchActionTypeDistribution();
        } else {
          this.actionTypeRows = [];
        }
      }
    },

    markPending() {
      this.pendingChanges = true;
    },

    async applyPending() {
      this.selectedDetail = null;
      this.pendingChanges = false;
      await this.refetchCurrentLevel();
    },

    onDateInputChange() {
      // Manually entered dates clear the active preset
      this.activeDatePreset = null;
      this.markPending();
    },

    _applyDatePreset(preset) {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const toISO = d => d.toISOString().split('T')[0];
      if (preset === 'mtd') {
        this.dateFrom = toISO(new Date(year, month, 1));
        this.dateTo = toISO(now);
      } else if (preset === 'ytd') {
        this.dateFrom = `${year}-01-01`;
        this.dateTo = toISO(now);
      } else if (preset === 'last_month') {
        this.dateFrom = toISO(new Date(year, month - 1, 1));
        this.dateTo = toISO(new Date(year, month, 0));
      }
      this.activeDatePreset = preset;
    },

    setDatePreset(preset) {
      this._applyDatePreset(preset);
      this.markPending();
    },

    datePresetStyle(key) {
      const active = this.activeDatePreset === key;
      return active
        ? { backgroundColor: '#FEF3F0', color: '#F76734', border: '1px solid #F76734', fontWeight: 600 }
        : { backgroundColor: '#F9FAFB', color: '#626264', border: '1px solid #E8E9E9' };
    },

    clearDateRange() {
      this.dateFrom = '';
      this.dateTo = '';
      this.activeDatePreset = null;
      this.markPending();
    },

    // -- Saved views --
    closeViewMenu() {
      this.showViewMenu = false;
      this.cancelSaveAs();
    },
    selectView(key) {
      const view = this.allViews.find(v => v.key === key);
      if (!view) return;
      this._applyViewState(view.state);
      this.currentViewKey = key;
      this.pendingChanges = false;
      this.selectedGroup = null;
      this.selectedDetail = null;
      this.showViewMenu = false;
      saveLastViewKey(key);
      this.refetchCurrentLevel();
    },
    _applyViewState(s) {
      this.grain = s.grain;
      this.groupChips = (s.groupChips || [])
        .map(k => resolveGroupChip(k, this.entityTypes))
        .filter(v => v != null);
      this.activeProgram = s.activeProgram ?? null;
      this.workTypeFilter = [...(s.workTypeFilter || [])];
      this.ownerFilter = [...(s.ownerFilter || [])];
      this.statusFilter = [...(s.statusFilter || [])];
      this.routingTierFilter = [...(s.routingTierFilter || [])];
      this.engagementFilter = [...(s.engagementFilter || [])];
      this.canCallOnly = s.canCallOnly !== false;
      this.overdueOnly = !!s.overdueOnly;
      this.unassignedOnly = !!s.unassignedOnly;
      this.staleOnly = !!s.staleOnly;
      if (s.activeDatePreset) {
        this._applyDatePreset(s.activeDatePreset);
      } else {
        this.activeDatePreset = null;
        this.dateFrom = s.dateFrom || '';
        this.dateTo = s.dateTo || '';
      }
    },
    _captureCurrentState() {
      return {
        grain: this.grain,
        groupChips: this.groupChips
          .map(c => serializeGroupChip(c, this.entityTypes))
          .filter(c => c != null),
        activeProgram: this.activeProgram,
        workTypeFilter: [...this.workTypeFilter],
        ownerFilter: [...this.ownerFilter],
        statusFilter: [...this.statusFilter],
        routingTierFilter: [...this.routingTierFilter],
        engagementFilter: [...this.engagementFilter],
        canCallOnly: this.canCallOnly,
        overdueOnly: this.overdueOnly,
        unassignedOnly: this.unassignedOnly,
        staleOnly: this.staleOnly,
        activeDatePreset: this.activeDatePreset,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      };
    },
    openSaveAs() {
      this.saveAsName = '';
      this.showSaveAsInput = true;
      this.$nextTick(() => {
        if (this.$refs.saveAsInput) this.$refs.saveAsInput.focus();
      });
    },
    cancelSaveAs() {
      this.showSaveAsInput = false;
      this.saveAsName = '';
    },
    confirmSaveAs() {
      const name = this.saveAsName.trim();
      if (!name) return;
      const key = `user_${Date.now()}`;
      this.userViews.push({ key, name, state: this._captureCurrentState() });
      saveUserViews(this.userViews);
      this.currentViewKey = key;
      this.pendingChanges = false;
      saveLastViewKey(key);
      this.cancelSaveAs();
      this.showViewMenu = false;
    },
    overwriteCurrentView() {
      if (!this.canSaveOverwrite) return;
      const idx = this.userViews.findIndex(v => v.key === this.currentViewKey);
      if (idx < 0) return;
      this.userViews[idx] = {
        ...this.userViews[idx],
        state: this._captureCurrentState(),
      };
      saveUserViews(this.userViews);
      this.pendingChanges = false;
      this.showViewMenu = false;
    },
    deleteUserView(key) {
      this.userViews = this.userViews.filter(v => v.key !== key);
      saveUserViews(this.userViews);
      if (this.currentViewKey === key) {
        this.currentViewKey = null;
        saveLastViewKey(null);
      }
    },

    setProgramTab(key) {
      if (this.activeProgram === key) return;
      this.activeProgram = key;
      // Program chip becomes a no-op when a specific program is active —
      // drop it from the grouping so we don't render a single-row group.
      if (key != null) {
        const idx = this.groupChips.indexOf('program');
        if (idx >= 0) this.groupChips.splice(idx, 1);
        if (this.groupChips.length === 0) {
          // Fall back to a grain-appropriate default so the table still
          // has a grouping dimension.
          this.groupChips = this.isOppGrain ? ['measure'] : ['owner'];
        }
      }
      this.selectedGroup = null;
      this.selectedDetail = null;
      this.refetchCurrentLevel();
    },

    setGrain(grain) {
      if (this.grain === grain) return;
      this.grain = grain;
      // Reset chips to the grain-appropriate default. Owner is useless on
      // Caseload for Vytalize today (no opp-level assignments are set), so
      // Program is the better default landing chip; falls back to Measure
      // when Program is hidden (specific program tab active).
      const allowed = new Set(this.groupByOptions.map(o => o.value));
      if (this.isOppGrain) {
        this.groupChips = allowed.has('program') ? ['program'] : ['measure'];
      } else {
        this.groupChips = ['owner'];
      }
      // Sort column defaults differ by grain — reset to a sensible default
      // that exists in the new column set.
      this.sortCol = this.isOppGrain ? 'opportunity_count' : 'this_week_count';
      this.sortDir = 'desc';
      this.selectedGroup = null;
      this.selectedDetail = null;
      this.refetchCurrentLevel();
    },

    onEntityFilterChanged(payload) {
      this.entityFilters = payload?.entity_filters || null;
      this.infoTagFilters = payload?.infotag_filters || null;
      this.myPractices = !!payload?.my_practices;
      this.markPending();
    },

    // -- Sorting --
    toggleSort(field) {
      if (this.sortCol === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortCol = field;
        this.sortDir = 'desc';
      }
    },
    sortIndicator(field) {
      if (this.sortCol !== field) return '';
      return this.sortDir === 'asc' ? '▲' : '▼';
    },
    sortData(data, col, dir) {
      const d = dir === 'asc' ? 1 : -1;
      const pick = col === '_delta' ? row => this.trendDelta(row) : row => row[col];
      return [...data].sort((a, b) => {
        const va = pick(a);
        const vb = pick(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === 'string') return d * va.localeCompare(vb);
        if (typeof va === 'boolean') return d * ((va ? 1 : 0) - (vb ? 1 : 0));
        return d * (va - vb);
      });
    },

    // -- Charts --
    renderWowChart() {
      const el = this.$refs.wowChart;
      if (!el) return;
      const svg = d3.select(el);
      svg.selectAll('*').remove();

      const width = el.clientWidth;
      const height = 180;
      if (!width) return;

      const margin = { top: 8, right: 8, bottom: 44, left: 32 };
      const innerW = width - margin.left - margin.right;
      const innerH = height - margin.top - margin.bottom;

      const root = svg
        .attr('viewBox', `0 0 ${width} ${height}`)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const drawAxes = (x, y) => {
        root.append('g')
          .call(d3.axisLeft(y).ticks(4).tickSize(-innerW))
          .call(sel => sel.select('.domain').remove())
          .call(sel => sel.selectAll('.tick line').attr('stroke', '#F3F4F6'))
          .call(sel => sel.selectAll('.tick text').attr('fill', '#9CA3AF').style('font-size', '10px'));

        root.append('g')
          .attr('transform', `translate(0,${innerH})`)
          .call(d3.axisBottom(x).tickFormat(d => d.length > 14 ? d.slice(0, 13) + '…' : d))
          .call(sel => sel.select('.domain').attr('stroke', '#E5E7EB'))
          .call(sel => sel.selectAll('.tick line').remove())
          .call(sel => sel.selectAll('.tick text')
            .attr('fill', '#6B7280')
            .style('font-size', '10px')
            .attr('transform', 'rotate(-20)')
            .style('text-anchor', 'end'));
      };

      if (this.isStacked) {
        const { rows, keys } = this.stackedChartData;
        if (!rows.length || !keys.length) return;

        const x = d3.scaleBand().domain(rows.map(r => r.group_1)).range([0, innerW]).padding(0.2);
        const yMax = d3.max(rows, r => keys.reduce((s, k) => s + (r[k] || 0), 0)) || 1;
        const y = d3.scaleLinear().domain([0, yMax]).nice().range([innerH, 0]);
        const colorByKey = Object.fromEntries(this.stackColors.map(s => [s.key, s.color]));

        drawAxes(x, y);

        const series = d3.stack().keys(keys)(rows);
        root.append('g').selectAll('g').data(series).enter().append('g')
          .attr('fill', d => colorByKey[d.key])
          .selectAll('rect').data(d => d.map(seg => ({ ...seg, key: d.key }))).enter().append('rect')
          .attr('x', seg => x(seg.data.group_1))
          .attr('y', seg => y(seg[1]))
          .attr('height', seg => Math.max(0, y(seg[0]) - y(seg[1])))
          .attr('width', x.bandwidth())
          .append('title')
          .text(seg => `${seg.data.group_1} · ${seg.key}: ${(seg[1] - seg[0]).toLocaleString()}`);
        return;
      }

      if (this.isOppGrain) {
        // Horizontal stacked bar per group_1 — each row is one cohort,
        // normalized to 100% so funnel composition is comparable across
        // groups regardless of count scale. Segments: Completed (green),
        // Worked-open (blue), Untouched (gray).
        const data = this.caseloadChartData;
        if (!data.length) return;

        // Caseload chart owns its own layout rather than reusing drawAxes
        // (which is tuned for vertical bars).
        root.selectAll('*').remove();
        const margin2 = { top: 4, right: 8, bottom: 22, left: 160 };
        const innerW2 = width - margin2.left - margin2.right;
        // Row height: accommodate up to 10 rows, minimum 20px each.
        const rowH = Math.max(18, Math.min(30, (innerH + margin.bottom - margin2.bottom) / data.length));
        const totalH = rowH * data.length + margin2.top + margin2.bottom;
        svg.attr('viewBox', `0 0 ${width} ${totalH}`)
           .attr('preserveAspectRatio', 'xMinYMin meet')
           .style('height', `${totalH}px`);
        const g2 = svg.append('g')
          .attr('transform', `translate(${margin2.left},${margin2.top})`);

        const x = d3.scaleLinear().domain([0, 1]).range([0, innerW2]);
        const y = d3.scaleBand()
          .domain(data.map(d => d.group_1))
          .range([0, rowH * data.length])
          .paddingInner(0.2);

        // Group labels (y-axis)
        g2.append('g')
          .call(d3.axisLeft(y).tickFormat(d => d.length > 22 ? d.slice(0, 21) + '…' : d))
          .call(sel => sel.select('.domain').remove())
          .call(sel => sel.selectAll('.tick line').remove())
          .call(sel => sel.selectAll('.tick text')
            .attr('fill', '#374151').style('font-size', '10px'));

        // Percentage axis (0%–100%)
        g2.append('g')
          .attr('transform', `translate(0,${rowH * data.length})`)
          .call(d3.axisBottom(x).ticks(5).tickFormat(d3.format('.0%')))
          .call(sel => sel.select('.domain').attr('stroke', '#E5E7EB'))
          .call(sel => sel.selectAll('.tick line').remove())
          .call(sel => sel.selectAll('.tick text')
            .attr('fill', '#9CA3AF').style('font-size', '9px'));

        const series = [
          { key: 'completed_count',   color: '#10B981', label: 'Completed' },
          { key: 'worked_open_count', color: '#3B82F6', label: 'Worked' },
          { key: 'untouched_count',   color: '#9CA3AF', label: 'Untouched' },
        ];

        for (const d of data) {
          const total = d.opportunity_count || 1; // avoid divide-by-zero
          let offset = 0;
          for (const s of series) {
            const frac = (d[s.key] || 0) / total;
            if (frac <= 0) continue;
            g2.append('rect')
              .attr('x', x(offset))
              .attr('y', y(d.group_1))
              .attr('width', Math.max(0, x(offset + frac) - x(offset)))
              .attr('height', y.bandwidth())
              .attr('fill', s.color)
              .append('title')
              .text(
                `${d.group_1} · ${s.label}: ${(d[s.key] || 0).toLocaleString()} `
                + `(${(frac * 100).toFixed(1)}%)`
              );
            offset += frac;
          }
          // Thin outline to help read zero-segment rows
          g2.append('rect')
            .attr('x', 0).attr('y', y(d.group_1))
            .attr('width', innerW2).attr('height', y.bandwidth())
            .attr('fill', 'none').attr('stroke', '#E5E7EB').attr('stroke-width', 0.5);
        }
        return;
      }

      const data = this.wowChartData;
      if (!data.length) return;

      const groups = data.map(d => d.group_1);
      const x0 = d3.scaleBand().domain(groups).range([0, innerW]).paddingInner(0.25);

      if (this.isDateFiltered) {
        // Filtered mode — single-series Actions bar. Week-over-week comparison
        // doesn't apply when the user has picked an explicit window.
        const yMax = d3.max(data, d => d.work_count) || 1;
        const y = d3.scaleLinear().domain([0, yMax]).nice().range([innerH, 0]);
        drawAxes(x0, y);
        root.selectAll('.bar').data(data).enter().append('rect')
          .attr('x', d => x0(d.group_1))
          .attr('y', d => y(d.work_count))
          .attr('width', x0.bandwidth())
          .attr('height', d => innerH - y(d.work_count))
          .attr('fill', '#3B82F6')
          .attr('rx', 2)
          .append('title')
          .text(d => `${d.group_1} · Actions: ${(d.work_count || 0).toLocaleString()}`);
        return;
      }

      const series = [
        { key: 'this_week_count', color: '#3B82F6' },
        { key: 'last_week_count', color: '#8B5CF6' },
        { key: 'two_weeks_ago_count', color: '#C4B5FD' },
      ];

      const x1 = d3.scaleBand().domain(series.map(s => s.key)).range([0, x0.bandwidth()]).padding(0.1);
      const yMax = d3.max(data, d => Math.max(
        d.this_week_count, d.last_week_count, d.two_weeks_ago_count, d.avg_per_week,
      )) || 1;
      const y = d3.scaleLinear().domain([0, yMax]).nice().range([innerH, 0]);

      drawAxes(x0, y);

      const groupG = root.selectAll('.bar-group').data(data).enter().append('g')
        .attr('transform', d => `translate(${x0(d.group_1)},0)`);

      for (const s of series) {
        groupG.append('rect')
          .attr('x', x1(s.key))
          .attr('y', d => y(d[s.key]))
          .attr('width', x1.bandwidth())
          .attr('height', d => innerH - y(d[s.key]))
          .attr('fill', s.color)
          .attr('rx', 2)
          .append('title')
          .text(d => `${d.group_1} · ${this.wowSeriesLabel(s.key)}: ${d[s.key].toLocaleString()}`);
      }

      // Avg/wk (6w) reference line — dashed green segment per group
      groupG.append('line')
        .attr('x1', 0)
        .attr('x2', x0.bandwidth())
        .attr('y1', d => y(d.avg_per_week))
        .attr('y2', d => y(d.avg_per_week))
        .attr('stroke', '#10B981')
        .attr('stroke-width', 1.75)
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-linecap', 'round')
        .append('title')
        .text(d => `${d.group_1} · Avg/wk (6w): ${Number(d.avg_per_week).toFixed(1)}`);
    },

    wowSeriesLabel(key) {
      if (key === 'this_week_count') return 'This Week';
      if (key === 'last_week_count') return 'Last Week';
      if (key === 'two_weeks_ago_count') return '2 Wks Ago';
      return 'Avg/wk (6w)';
    },

    // -- Trend (This Week vs Last Wk @ Now) --
    trendDelta(row) {
      return (row.this_week_count || 0) - (row.last_week_at_this_time_count || 0);
    },
    trendArrow(row) {
      const d = this.trendDelta(row);
      if (d > 0) return '↑';
      if (d < 0) return '↓';
      return '';
    },
    trendClass(row) {
      const d = this.trendDelta(row);
      if (d > 0) return 'text-emerald-600 font-semibold';
      if (d < 0) return 'text-red-600 font-semibold';
      return '';
    },

    /** Copy the action-type table as TSV so it pastes straight into Excel. */
    async copyActionTypeTable() {
      const lines = ['Action Type\tActions\t% of Total'];
      this.actionTypeTable.forEach(r => lines.push(`${r.label}\t${r.count}\t${r.pct}%`));
      lines.push(`Total\t${this.actionTypeTotal}\t100%`);
      const tsv = lines.join('\n');
      try {
        await navigator.clipboard.writeText(tsv);
      } catch (e) {
        // Clipboard API needs a secure context; fall back to a temp textarea.
        const ta = document.createElement('textarea');
        ta.value = tsv;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      this.actionTypeCopied = true;
      setTimeout(() => { this.actionTypeCopied = false; }, 1500);
    },

    renderDonut() {
      const el = this.$refs.donutChart;
      if (!el) return;
      const data = this.donutData;
      const svg = d3.select(el);
      svg.selectAll('*').remove();
      if (!data.length) return;

      const size = 140;
      const radius = size / 2;

      const arc = d3.arc().innerRadius(radius * 0.58).outerRadius(radius - 2);
      const pie = d3.pie().value(d => d.work_count || 0).sort(null);

      const g = svg
        .attr('viewBox', `0 0 ${size} ${size}`)
        .append('g')
        .attr('transform', `translate(${radius},${radius})`);

      g.selectAll('path').data(pie(data)).enter().append('path')
        .attr('d', arc)
        .attr('fill', (d, i) => DONUT_COLORS[i % DONUT_COLORS.length])
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .append('title')
        .text(d => `${d.data.group_1 || 'Unassigned'}: ${(d.data.work_count || 0).toLocaleString()}`);
    },

    // -- Cell formatting --
    formatCell(col, value) {
      if (value == null || value === '') return '';
      if (col.format === 'boolean') return value ? 'Yes' : '';
      if (typeof value === 'number') return Number(value).toLocaleString();
      return String(value);
    },
    formatDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      return isNaN(d) ? v : d.toISOString().split('T')[0];
    },
    formatDateTime(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (isNaN(d)) return v;
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    formatDuration(seconds) {
      if (seconds == null) return '—';
      const s = Number(seconds);
      if (s < 60) return `${Math.round(s)}s`;
      if (s < 3600) return `${Math.round(s / 60)}m`;
      return `${(s / 3600).toFixed(1)}h`;
    },

    // -- Labels --
    programLabel(p) {
      if (p === 'quality_gaps') return 'Quality Gaps';
      if (p === 'medication_adherence') return 'Med Adherence';
      return p || '—';
    },

    // -- Export --
    csvEscape(val) {
      if (val == null) return '';
      const str = String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str;
    },
    async exportCsv() {
      // Export returns the full dataset — paginated list view would silently
      // truncate audit-log dumps at endRow (500 rows by default).
      this.exportLoading = true;
      try {
        const payload = this._buildPayload({ export: true });
        if (this.selectedGroup) {
          payload.groupKeys = this.groupChips.map((_, i) => {
            const idCol = `group_${i + 1}_id`;
            return this.selectedGroup[idCol] != null
              ? this.selectedGroup[idCol]
              : this.selectedGroup[`group_${i + 1}`];
          });
        } else {
          payload.groupKeys = null;
        }
        const res = await this.$axios.post('/api/quality/operations/', payload);
        const rawRows = (res.data && res.data.rows) || [];
        const rows = this.sortCol
          ? this.sortData(rawRows, this.sortCol, this.sortDir)
          : rawRows;
        const cols = this.activeColumns;
        const header = cols.map(c => c.header).join(',');
        const body = rows.map(row =>
          cols.map(c => {
            if (c.field === '_delta') {
              return this.csvEscape(this.trendDelta(row));
            }
            return this.csvEscape(this.formatCell(c, row[c.field]));
          }).join(',')
        ).join('\n');
        const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const scope = this.showGroupTable ? 'groups' : 'work';
        a.download = `quality_ops_${scope}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('CSV export failed:', error);
      } finally {
        this.exportLoading = false;
      }
    },
  },

  async mounted() {
    // Load entity types for Practice group option
    try {
      const etResponse = await this.$axios.get('/api/entity-types/', { params: { include_counts: true } });
      this.entityTypes = (etResponse.data || []).filter(et => et.entity_count > 0);
    } catch (e) {
      console.error('Failed to load entity types:', e);
    }

    // Load work types + owners + engagement scores for filter dropdowns.
    // Engagement scores are the per-practice composite (one row per practice
    // in the tenant); the Caseload filter buckets them client-side.
    try {
      const [wtRes, ownerRes, engRes] = await Promise.all([
        this.$axios.get('/api/quality/operations/', { params: { lookup: 'work_types' } }),
        this.$axios.get('/api/quality/operations/', { params: { lookup: 'owners' } }),
        this.$axios.get('/api/quality/operations/', { params: { lookup: 'engagement_scores' } }),
      ]);
      this.workTypes = wtRes.data.work_types || [];
      this.owners = ownerRes.data.owners || [];
      this.engagementScores = engRes.data.engagement_scores || [];
    } catch (e) {
      console.error('Failed to load filter lookups:', e);
    }

    // Restore last-used view if still valid; otherwise stay on data() defaults.
    const lastKey = loadLastViewKey();
    const lastView = lastKey ? this.allViews.find(v => v.key === lastKey) : null;
    if (lastView) {
      this._applyViewState(lastView.state);
      this.currentViewKey = lastKey;
    }

    // Donut is work-grain only — skip the fetch on Opportunities, where
    // groupBy=['work_type'] isn't a valid dim and would 500. Mirrors the
    // guard already present in refetchCurrentLevel.
    await Promise.all([
      this.fetchGroups(),
      ...(this.isOppGrain ? [] : [this.fetchActionTypeDistribution()]),
    ]);
  },
};
</script>
